/**
 * Keeping a stack's zoom meaningful when its images are not all the same size.
 *
 * cornerstone keeps the viewport (scale, translation) while a stack scrolls, so the next instance of a
 * different matrix (a CR/DX/US series, an MR series with a localizer) comes out cropped or tiny. On a
 * size change the zoom is carried over *relative to the fit*: an image the user saw fitted is shown
 * fitted, one zoomed 2x stays 2x the fit, and the canvas offset of a pan is kept. Same-size slices
 * (the normal case) are left alone, so ordinary scrolling never touches the zoom.
 */

export interface ImageGeometry {
  rows: number;
  columns: number;
  rowPixelSpacing?: number;
  columnPixelSpacing?: number;
}

export interface ViewTransform {
  scale: number;
  translation: { x: number; y: number };
  rotation: number;
}

export interface CanvasSize {
  width: number;
  height: number;
}

/** cornerstone-core 2.6.1 getImageFitScale: the scale at which the whole image fits the canvas. */
export function fitScale(canvas: CanvasSize, image: ImageGeometry, rotation: number): number {
  const quarterTurn = Math.abs(Math.round(rotation / 90)) % 2 === 1;
  const width = quarterTurn ? image.rows : image.columns;
  const height = quarterTurn ? image.columns : image.rows;
  const rowSpacing = image.rowPixelSpacing || 1;
  const columnSpacing = image.columnPixelSpacing || 1;
  const horizontalRatio = rowSpacing < columnSpacing ? columnSpacing / rowSpacing : 1;
  const verticalRatio = rowSpacing < columnSpacing ? 1 : rowSpacing / columnSpacing;
  return Math.min(canvas.height / height / verticalRatio, canvas.width / width / horizontalRatio);
}

export function sameGeometry(a: ImageGeometry, b: ImageGeometry): boolean {
  return a.rows === b.rows && a.columns === b.columns
    && (a.rowPixelSpacing || 1) === (b.rowPixelSpacing || 1)
    && (a.columnPixelSpacing || 1) === (b.columnPixelSpacing || 1);
}

/** The transform for `next` that keeps what the user saw of `previous`, or null when nothing changes. */
export function carryTransform(canvas: CanvasSize, previous: ImageGeometry, next: ImageGeometry, current: ViewTransform): ViewTransform | null {
  if (sameGeometry(previous, next)) return null;
  const before = fitScale(canvas, previous, current.rotation);
  const after = fitScale(canvas, next, current.rotation);
  if (!(before > 0) || !(after > 0) || !(current.scale > 0)) return null;
  const scale = current.scale * (after / before);
  const keep = current.scale / scale;
  return { scale, translation: { x: current.translation.x * keep, y: current.translation.y * keep }, rotation: current.rotation };
}
